import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { validateNIF, validatePhone } from '../utils/ptValidators';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { StatusBadge } from '../components/StatusBadge';
import { DoubleScroll } from '../components/DoubleScroll';
import { LeadKanban } from '../components/LeadKanban';
import { IncontactavelDialog } from '../components/IncontactavelDialog';
import { CallOutcomeDialog } from '../components/CallOutcomeDialog';
import { LeadAddressFields } from '../components/LeadAddressFields';
import { restricoesResumo } from '../lib/restricoes';
import { LEAD_STATUS_CONFIG, LEAD_SECTOR_CONFIG, leadSector } from '../lib/leadStatus';
import { HelpButton } from '../components/HelpButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Search,
  Plus,
  Upload,
  AlertTriangle,
  Clock,
  CalendarRange,
  Sparkles,
  Phone,
  Loader2,
  Download,
} from 'lucide-react';
import { format, parseISO, isBefore } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// SOURCE_LABELS é dinâmico — carregado do backend (/leads/config/sources) no mount.
// Inclui builtin + customizadas. Mantemos fallback estático para resiliência.
const FALLBACK_SOURCE_LABELS = {
  call_center: '📞 Call Center',
  indicacao: '🤝 Indicação',
  listagem: '📋 Listagem',
  landing_page: '🌐 Landing Page',
  meu_cliente: '👤 Meu Cliente',
  angariador: '🚶 Angariador',
  meta: '📱 Meta (Facebook/Instagram)',
  outro: '🔖 Outro',
};

const STATUS_LABELS = {
  nova: 'Nova',
  em_contacto: 'Em Contacto',
  reagendada: 'Reagendada',
  incontactavel: 'Incontactável',
  proposta: 'Proposta',
  convertida: 'Convertida',
  sem_interesse: 'Sem Interesse',
  invalida: 'Inválida',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), "dd MMM 'às' HH:mm", { locale: pt });
  } catch {
    return iso;
  }
};

const isOverdue = (iso) => {
  if (!iso) return false;
  try { return isBefore(parseISO(iso), new Date()); } catch { return false; }
};

const daysSinceCreated = (iso) => {
  if (!iso) return 0;
  try { return Math.floor((Date.now() - parseISO(iso).getTime()) / 86400000); } catch { return 0; }
};
const fmtDay = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), "dd MMM", { locale: pt }); } catch { return iso; }
};

export const LeadPipelinePage = () => {
  const { user } = useAuth();
  const canFilterByOwner = ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setSearchParams] = useSearchParams();

  const [data, setData] = useState({ leads: [], counters: { total: 0, atrasados: 0, hoje: 0, semana: 0, novos: 0, by_status: {}, by_source: {} } });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(() => params.get('status') || 'all');
  const [filterSource, setFilterSource] = useState(() => params.get('source') || 'all');
  const [filterPartner, setFilterPartner] = useState(() => params.get('partner') || 'all');
  const [filterComercial, setFilterComercial] = useState(() => params.get('comercial') || 'all');
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [comercialOptions, setComercialOptions] = useState([]);
  const [filterSegment, setFilterSegment] = useState(() => params.get('segment') || 'all'); // all | atrasados | hoje | semana | novos
  const [filterSector, setFilterSector] = useState(() => params.get('sector') || 'all'); // all | energia | telecom | alarmes | outro
  const [showClosed, setShowClosed] = useState(() => (params.get('closed') || localStorage.getItem('pipeline_show_closed')) === '1');
  const [search, setSearch] = useState(() => params.get('q') || '');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLead, setNewLead] = useState({
    client_name: '', client_phone: '', client_email: '',
    client_nif: '', source: 'call_center', notes: '', angariador_id: '', service_interest: '',
    previous_commercializer_id: '', previous_contract_date: '',
  });
  const [previousContractFile, setPreviousContractFile] = useState(null);
  const [angariadorOptions, setAngariadorOptions] = useState([]);
  const [commercializerOptions, setCommercializerOptions] = useState([]);

  // Dynamic source labels (builtin + custom) loaded from backend
  const [SOURCE_LABELS, setSourceLabels] = useState(FALLBACK_SOURCE_LABELS);
  const canManageSources = ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const [newSourceOpen, setNewSourceOpen] = useState(false);
  const [newSourceLabel, setNewSourceLabel] = useState('');
  const [creatingSource, setCreatingSource] = useState(false);

  const reloadSources = async () => {
    try {
      const r = await axios.get(`${API_URL}/leads/config/sources`);
      if (r.data && typeof r.data === 'object') setSourceLabels(r.data);
    } catch (err) {
      console.error('Failed to load lead sources', err);
    }
  };

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/leads/config/sources`)
      .then(r => { if (alive && r.data) setSourceLabels(r.data); })
      .catch(err => console.error('Failed to load lead sources', err));
    return () => { alive = false; };
  }, []);

  const handleCreateSource = async () => {
    const label = newSourceLabel.trim();
    if (!label) { toast.error('Insere um nome'); return; }
    setCreatingSource(true);
    try {
      const r = await axios.post(`${API_URL}/leads/config/sources`, { label });
      toast.success(`Origem "${label}" criada`);
      await reloadSources();
      setNewLead(prev => ({ ...prev, source: r.data.key }));
      setNewSourceOpen(false);
      setNewSourceLabel('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar origem');
    } finally {
      setCreatingSource(false);
    }
  };

  // Only back-office roles can attribute an angariador to a manual lead.
  const canAttributeAngariador = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice'].includes(user?.role);

  useEffect(() => {
    if (!canAttributeAngariador) return;
    axios.get(`${API_URL}/users`)
      .then(r => setAngariadorOptions((r.data || []).filter(u => u.role === 'angariador' && u.active !== false)))
      .catch(() => setAngariadorOptions([]));
  }, [canAttributeAngariador]);

  // Carregar comercializadoras (para o campo "Comercializadora Anterior" quando source=meu_cliente)
  useEffect(() => {
    axios.get(`${API_URL}/simulador/commercializers`)
      .then(r => setCommercializerOptions((r.data || []).filter(c => c.active !== false)))
      .catch(() => setCommercializerOptions([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = {};
      if (filterStatus !== 'all') queryParams.status = filterStatus;
      if (filterSource !== 'all') queryParams.source = filterSource;
      const res = await axios.get(`${API_URL}/leads/pipeline`, { params: queryParams });
      setData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar pipeline');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSource]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useRefreshListener(fetchData);

  // Vista: lista | kanban (persistida no URL + localStorage)
  const [viewMode, setViewMode] = useState(() => params.get('view') || localStorage.getItem('pipeline_view') || 'lista');
  const setView = (v) => { setViewMode(v); localStorage.setItem('pipeline_view', v); };

  // Sincroniza os filtros com o URL para que "Voltar" (da ficha da lead) restaure o estado
  useEffect(() => {
    if (params.get('lead')) return; // deep-link para uma lead é tratado noutro efeito
    const p = {};
    if (filterStatus !== 'all') p.status = filterStatus;
    if (filterSource !== 'all') p.source = filterSource;
    if (filterPartner !== 'all') p.partner = filterPartner;
    if (filterComercial !== 'all') p.comercial = filterComercial;
    if (filterSegment !== 'all') p.segment = filterSegment;
    if (filterSector !== 'all') p.sector = filterSector;
    if (search) p.q = search;
    if (viewMode && viewMode !== 'lista') p.view = viewMode;
    if (showClosed) p.closed = '1';
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterSource, filterPartner, filterComercial, filterSegment, filterSector, search, viewMode, showClosed]);

  // Restaura a posição de scroll ao voltar da ficha da lead (restauro manual: o conteúdo é assíncrono)
  const scrollDone = useRef(false);
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);
  useEffect(() => {
    if (loading || scrollDone.current) return;
    if (!data.leads || data.leads.length === 0) return; // espera o conteúdo renderizar
    const raw = sessionStorage.getItem('pipeline_scroll');
    if (raw == null) return;
    const target = parseInt(raw, 10) || 0;
    scrollDone.current = true;
    sessionStorage.removeItem('pipeline_scroll');
    if (target <= 0) return;
    let tries = 0;
    const iv = setInterval(() => {
      window.scrollTo(0, target);
      tries += 1;
      if (Math.abs((window.scrollY || window.pageYOffset || 0) - target) <= 4 || tries > 20) clearInterval(iv);
    }, 90);
  }, [loading, data.leads]);

  const [incLead, setIncLead] = useState(null);
  const [callLead, setCallLead] = useState(null);
  const handleStatusChange = async (lead, newStatus, notes) => {
    if (newStatus === 'incontactavel' && !notes) { setIncLead(lead); return; }
    try {
      await axios.put(`${API_URL}/leads/${lead.id}/status`, { status: newStatus, notes });
      toast.success(`${lead.client_name} → ${LEAD_STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao mudar estado');
    }
  };
  const openLead = (lead) => {
    try { sessionStorage.setItem('pipeline_scroll', String(window.scrollY || window.pageYOffset || 0)); } catch (e) {}
    navigate(`/leads/${lead.id}`, { state: { returnTo: location.pathname + location.search } });
  };
  const convertLead = (lead) => { toast.info('Abre a ficha e usa "Converter em Contrato" para fechar.'); openLead(lead); };

  // Owner filter options (partner + commercial) — only for privileged roles
  useEffect(() => {
    if (!canFilterByOwner) return;
    axios.get(`${API_URL}/leads/assignable-partners`).then(r => setPartnerOptions(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/leads/assignable-users`).then(r => setComercialOptions((r.data || []).filter(u => ['comercial', 'chefe_equipa'].includes(u.role)))).catch(() => {});
  }, [canFilterByOwner]);

  // Highlight specific lead from URL (e.g. ?lead=ID from notification)
  useEffect(() => {
    const leadId = params.get('lead');
    if (leadId) navigate(`/leads/${leadId}`);
  }, [params, navigate]);

  const filteredLeads = useMemo(() => {
    let leads = data.leads;
    if (filterPartner !== 'all') leads = leads.filter(l => l.partner_id === filterPartner);
    if (filterComercial !== 'all') leads = leads.filter(l => l.assigned_to === filterComercial);
    if (filterSector !== 'all') leads = leads.filter(l => leadSector(l.service_interest) === filterSector);
    if (filterSegment !== 'all') {
      const now = new Date();
      const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
      const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7);
      leads = leads.filter(l => {
        const s = l.status;
        if (filterSegment === 'novos') return s === 'nova';
        if (s === 'convertida' || s === 'sem_interesse' || s === 'invalida') return false;
        const nxt = l.next_action_at ? new Date(l.next_action_at) : null;
        if (!nxt) return false;
        if (filterSegment === 'atrasados') return nxt < now;
        if (filterSegment === 'hoje') return nxt >= now && nxt <= todayEnd;
        if (filterSegment === 'semana') return nxt > todayEnd && nxt <= weekEnd;
        return true;
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      leads = leads.filter(l =>
        (l.client_name || '').toLowerCase().includes(q) ||
        (l.client_phone || '').toLowerCase().includes(q) ||
        (l.client_email || '').toLowerCase().includes(q) ||
        (l.client_nif || '').toLowerCase().includes(q)
      );
    }
    // Novas: mais recentes (criação/atribuição) primeiro
    const recent = (l) => l.assigned_at || l.created_at || '';
    const novas = leads.filter(l => l.status === 'nova').sort((a, b) => recent(b).localeCompare(recent(a)));
    return [...novas, ...leads.filter(l => l.status !== 'nova')];
  }, [data.leads, filterSegment, search, filterPartner, filterComercial, filterSector]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLead.client_name.trim() || !newLead.client_phone.trim()) {
      toast.error('Nome e telefone obrigatórios');
      return;
    }
    // Strict PT validation
    const nifCheck = validateNIF(newLead.client_nif);
    if (!nifCheck.ok) { toast.error(`NIF: ${nifCheck.error}`); return; }
    const phCheck = validatePhone(newLead.client_phone, newLead.client_phone_tipo || 'telemovel');
    if (!phCheck.ok) { toast.error(`Telefone: ${phCheck.error}`); return; }
    if (!newLead.service_interest) { toast.error('Serviço de interesse é obrigatório (setor).'); return; }
    // Validação "Meu Cliente": comercializadora anterior obrigatória
    if (newLead.source === 'meu_cliente' && !newLead.previous_commercializer_id) {
      toast.error('Selecciona a Comercializadora Anterior');
      return;
    }
    setCreating(true);
    try {
      const payload = { ...newLead };
      if (!payload.angariador_id) delete payload.angariador_id;
      if (payload.source !== 'meu_cliente') {
        delete payload.previous_commercializer_id;
        delete payload.previous_contract_date;
      }
      const res = await axios.post(`${API_URL}/leads/manual`, payload);
      const newLeadId = res.data?.id;
      // Upload do PDF se fornecido (só faz sentido em "meu_cliente")
      if (newLeadId && previousContractFile && newLead.source === 'meu_cliente') {
        try {
          const fd = new FormData();
          fd.append('file', previousContractFile);
          await axios.post(`${API_URL}/leads/${newLeadId}/previous-contract`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (upErr) {
          toast.warning(
            'Lead criada, mas o PDF do contrato anterior não foi carregado. ' +
            (upErr?.response?.data?.detail || 'Tenta novamente nos detalhes da lead.')
          );
        }
      }
      toast.success('Lead criada');
      setCreateOpen(false);
      setNewLead({
        client_name: '', client_phone: '', client_email: '', client_nif: '',
        client_address: '', client_door: '', client_floor: '', client_side: '', client_postal_code: '', client_locality: '',
        source: 'call_center', notes: '', angariador_id: '', service_interest: '',
        previous_commercializer_id: '', previous_contract_date: '',
      });
      setPreviousContractFile(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar lead');
    } finally {
      setCreating(false);
    }
  };

  const segments = [
    { key: 'atrasados', label: 'Atrasadas', icon: AlertTriangle, color: 'rose', count: data.counters.atrasados },
    { key: 'hoje', label: 'Hoje', icon: Clock, color: 'amber', count: data.counters.hoje },
    { key: 'semana', label: 'Esta semana', icon: CalendarRange, color: 'sky', count: data.counters.semana },
    { key: 'novos', label: 'Novas', icon: Sparkles, color: 'emerald', count: data.counters.novos },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="lead-pipeline-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Phone className="w-6 h-6 text-[#2D3E6F]" />
              Minha Pipeline
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              {data.counters.total} lead{data.counters.total === 1 ? '' : 's'} no total · Trabalha cada contacto e converte em contrato com 1 clique.
            </p>
          </div>
          <div className="flex gap-2">
            <HelpButton category="leads" />
            {(() => {
              // Importar — só super_admin, gestor, admin, parceiro (mas não admins de parceiros lead-generator-only)
              const role = user?.role;
              const lwp = user?.partner_permissions?.lead_workers_partner_ids;
              const isLeadGeneratorOnly = ['admin', 'parceiro'].includes(role) && Array.isArray(lwp) && lwp.length > 0;
              const canImport = ['super_admin', 'gestor', 'admin', 'parceiro'].includes(role) && !isLeadGeneratorOnly;
              if (!canImport) return null;
              return (
                <Button
                  variant="outline"
                  onClick={() => navigate('/leads/importar')}
                  className="rounded-full gap-2"
                  data-testid="import-leads-btn"
                >
                  <Upload className="w-4 h-4" />
                  Importar
                </Button>
              );
            })()}
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2"
              data-testid="create-lead-btn"
            >
              <Plus className="w-4 h-4" />
              Nova Lead
            </Button>
          </div>
        </div>

        {/* Segment cards (clickable filters) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {segments.map(seg => {
            const Icon = seg.icon;
            const active = filterSegment === seg.key;
            const colorMap = {
              rose: { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500', activeBg: 'bg-rose-500', activeText: 'text-white' },
              amber: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', activeBg: 'bg-amber-500', activeText: 'text-white' },
              sky: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500', activeBg: 'bg-sky-500', activeText: 'text-white' },
              emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', activeBg: 'bg-emerald-500', activeText: 'text-white' },
            }[seg.color];
            return (
              <button
                key={seg.key}
                type="button"
                onClick={() => setFilterSegment(active ? 'all' : seg.key)}
                className={`text-left p-4 rounded-2xl border transition-all ${
                  active
                    ? `${colorMap.activeBg} ${colorMap.activeText} border-transparent shadow-md`
                    : `${colorMap.bg} ${colorMap.text} border-slate-100 hover:border-slate-300`
                }`}
                data-testid={`segment-${seg.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`w-5 h-5 ${active ? 'text-white' : ''}`} />
                  <span className={`text-3xl font-bold ${active ? 'text-white' : ''}`}>{seg.count}</span>
                </div>
                <p className={`text-sm font-semibold ${active ? 'text-white' : ''}`}>{seg.label}</p>
              </button>
            );
          })}
        </div>

        {/* Filters bar */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex flex-wrap items-end gap-3" data-testid="pipeline-filters">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Pesquisar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nome, telefone, email, NIF..."
                  className="h-9 pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Estado</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Origem</Label>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="h-9" data-testid="filter-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="internas">🏢 Internas (todas exceto angariador)</SelectItem>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Setor</Label>
              <Select value={filterSector} onValueChange={setFilterSector}>
                <SelectTrigger className="h-9" data-testid="filter-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(LEAD_SECTOR_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {canFilterByOwner && (
              <div className="min-w-[150px]">
                <Label className="text-xs text-slate-500 mb-1 block">Parceiro</Label>
                <Select value={filterPartner} onValueChange={setFilterPartner}>
                  <SelectTrigger className="h-9" data-testid="filter-partner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {partnerOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {canFilterByOwner && (
              <div className="min-w-[150px]">
                <Label className="text-xs text-slate-500 mb-1 block">Comercial</Label>
                <Select value={filterComercial} onValueChange={setFilterComercial}>
                  <SelectTrigger className="h-9" data-testid="filter-comercial"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {comercialOptions.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}{u.role === 'chefe_equipa' ? ' (Chefe Equipa)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(filterStatus !== 'all' || filterSource !== 'all' || filterSegment !== 'all' || filterSector !== 'all' || filterPartner !== 'all' || filterComercial !== 'all' || search) && (
              <Button variant="ghost" onClick={() => { setFilterStatus('all'); setFilterSource('all'); setFilterSegment('all'); setFilterSector('all'); setFilterPartner('all'); setFilterComercial('all'); setSearch(''); }} className="h-9">
                Limpar
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Toggle de vista */}
        <div className="flex items-center justify-end gap-2">
          {viewMode === 'kanban' && (
            <button
              onClick={() => { const v = !showClosed; setShowClosed(v); localStorage.setItem('pipeline_show_closed', v ? '1' : '0'); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${showClosed ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'}`}
              data-testid="toggle-closed-btn"
            >{showClosed ? 'Ocultar fechadas' : 'Ver fechadas'}</button>
          )}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="pipeline-view-toggle">
            <button
              onClick={() => setView('lista')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'lista' ? 'bg-[#2D3E6F] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="view-lista-btn"
            >Lista</button>
            <button
              onClick={() => setView('kanban')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-[#2D3E6F] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="view-kanban-btn"
            >Kanban</button>
          </div>
        </div>

        <CallOutcomeDialog lead={callLead} onClose={() => setCallLead(null)} onSaved={fetchData} />
        <IncontactavelDialog lead={incLead} onClose={() => setIncLead(null)} onConfirm={(note) => { const l = incLead; setIncLead(null); handleStatusChange(l, 'incontactavel', note); }} />

        {/* Kanban */}
        {viewMode === 'kanban' && (
          loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <LeadKanban
              leads={filteredLeads}
              onStatusChange={handleStatusChange}
              onConvert={convertLead}
              onOpen={openLead}
              onCall={setCallLead}
              showOwner={canFilterByOwner}
              showClosed={showClosed}
            />
          )
        )}

        {/* Table */}
        {viewMode === 'lista' && (
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filteredLeads.length === 0 ? (
            <CardContent className="text-center py-16">
              <Phone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-400">Nenhuma lead corresponde aos filtros</p>
            </CardContent>
          ) : (
            <DoubleScroll>
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Contacto</TableHead>
                    <TableHead className="font-semibold">Origem</TableHead>
                    <TableHead className="font-semibold">Comercial</TableHead>
                    <TableHead className="font-semibold">Carregada</TableHead>
                    <TableHead className="font-semibold">Estado</TableHead>
                    <TableHead className="font-semibold">Próxima ação</TableHead>
                    <TableHead className="font-semibold">Último contacto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead, idx) => {
                    const overdue = lead.next_action_at && isOverdue(lead.next_action_at) && !['convertida', 'sem_interesse', 'invalida'].includes(lead.status);
                    return (
                      <TableRow
                        key={lead.id}
                        className={`hover:bg-slate-50/70 cursor-pointer ${overdue ? 'bg-rose-50/40' : ''}`}
                        onClick={() => openLead(lead)}
                        data-testid={`lead-row-${idx}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {overdue && <span className="inline-block w-2 h-2 rounded-full bg-rose-500 animate-pulse" title="Atrasada" />}
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-medium text-slate-800">{lead.client_name}</p>
                                {lead.restricoes?.length > 0 && <span className="inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white" data-testid={`list-restricao-${lead.id}`}>{restricoesResumo(lead.restricoes)}</span>}
                                {leadSector(lead.service_interest) && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${LEAD_SECTOR_CONFIG[leadSector(lead.service_interest)].badge}`} title={`Setor: ${LEAD_SECTOR_CONFIG[leadSector(lead.service_interest)].label}`}>
                                    {LEAD_SECTOR_CONFIG[leadSector(lead.service_interest)].label}
                                  </span>
                                )}
                                {lead.service_type === 'dual' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200 font-semibold" title="Luz + Gás (CPE + CUI)">✨ L+G</span>
                                )}
                                {lead.service_type === 'gas' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200 font-semibold" title="Gás (CUI)">🔥 Gás</span>
                                )}
                                {lead.service_type === 'luz' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 font-semibold" title="Luz (CPE)">⚡ Luz</span>
                                )}
                              </div>
                              {lead.client_nif && <p className="text-xs text-slate-400">NIF {lead.client_nif}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="text-slate-700 font-mono">{lead.client_phone}</p>
                            {lead.client_email && <p className="text-xs text-slate-400">{lead.client_email}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {lead.angariador_id ? (
                            <span
                              className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
                              title={`Lead externa submetida por ${lead.angariador_name || lead.angariador_email || 'Angariador'}`}
                            >
                              <span>🤝</span>
                              <span className="truncate max-w-[120px]">{lead.angariador_name || lead.angariador_email || 'Angariador'}</span>
                            </span>
                          ) : (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                              {SOURCE_LABELS[lead.source] || 'Outro'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.assigned_to_name ? (
                            <div className="text-sm min-w-0">
                              <p className="text-slate-700 font-medium truncate max-w-[150px]">{lead.assigned_to_name}</p>
                              {lead.partner_name && <p className="text-xs text-slate-400 truncate max-w-[150px]">{lead.partner_name}</p>}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">— Por atribuir —</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-600" title={lead.created_at ? new Date(lead.created_at).toLocaleString('pt-PT') : ''} data-testid={`lead-loaded-${idx}`}>
                            <p>{fmtDay(lead.created_at)}</p>
                            {lead.contact_attempts === 0 && lead.status === 'nova' && daysSinceCreated(lead.created_at) >= 2 && (
                              <span className={`text-[10px] font-bold ${daysSinceCreated(lead.created_at) >= 5 ? 'text-red-600' : 'text-orange-600'}`}>Sem ação há {daysSinceCreated(lead.created_at)}d</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={lead.status} />
                        </TableCell>
                        <TableCell className={`text-sm ${overdue ? 'text-rose-700 font-semibold' : 'text-slate-600'}`}>
                          {fmtDate(lead.next_action_at)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {fmtDate(lead.last_contact_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DoubleScroll>
          )}
        </Card>
        )}
      </div>

      {/* Create Lead Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-lead-dialog">
          <DialogHeader>
            <DialogTitle>Nova Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Nome do cliente *</Label>
              <Input
                value={newLead.client_name}
                onChange={(e) => setNewLead({ ...newLead, client_name: e.target.value })}
                required
                data-testid="new-lead-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefone *</Label>
                <div className="flex gap-2">
                  <Select
                    value={newLead.client_phone_tipo || 'telemovel'}
                    onValueChange={(v) => setNewLead({ ...newLead, client_phone_tipo: v })}
                  >
                    <SelectTrigger className="w-28" data-testid="new-lead-phone-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="telemovel">Telemóvel</SelectItem>
                      <SelectItem value="fixo">Fixo</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={newLead.client_phone}
                    onChange={(e) => setNewLead({ ...newLead, client_phone: e.target.value })}
                    required
                    data-testid="new-lead-phone"
                    maxLength={9}
                    placeholder={(newLead.client_phone_tipo === 'fixo') ? '211234567' : '912345678'}
                  />
                </div>
                {newLead.client_phone && !validatePhone(newLead.client_phone, newLead.client_phone_tipo || 'telemovel').ok && (
                  <p className="text-xs text-rose-600 mt-1">
                    {validatePhone(newLead.client_phone, newLead.client_phone_tipo || 'telemovel').error}
                  </p>
                )}
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={newLead.client_email}
                  onChange={(e) => setNewLead({ ...newLead, client_email: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>NIF</Label>
                <Input
                  value={newLead.client_nif}
                  onChange={(e) => setNewLead({ ...newLead, client_nif: e.target.value })}
                  maxLength={9}
                  placeholder="123456789"
                />
                {newLead.client_nif && !validateNIF(newLead.client_nif).ok && (
                  <p className="text-xs text-rose-600 mt-1">{validateNIF(newLead.client_nif).error}</p>
                )}
              </div>
              <div className="col-span-2">
                <LeadAddressFields value={newLead} onChange={(p) => setNewLead((prev) => ({ ...prev, ...p }))} testPrefix="new-lead" />
              </div>
              <div>
                <Label>Origem *</Label>
                <Select
                  value={newLead.source}
                  onValueChange={(v) => {
                    if (v === '__create__') { setNewSourceOpen(true); return; }
                    setNewLead({ ...newLead, source: v });
                  }}
                >
                  <SelectTrigger data-testid="new-lead-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                    {canManageSources && (
                      <SelectItem value="__create__" className="text-[#2D3E6F] font-medium border-t mt-1 pt-2">
                        ➕ Criar nova origem...
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Serviço de interesse <span className="text-rose-500">*</span></Label>
              <Select
                value={newLead.service_interest}
                onValueChange={(v) => setNewLead({ ...newLead, service_interest: v })}
              >
                <SelectTrigger data-testid="new-lead-service" className={!newLead.service_interest ? 'border-rose-300' : ''}>
                  <SelectValue placeholder="Selecionar setor..." />
                </SelectTrigger>
                <SelectContent>
                  {["Luz e Gas", "Solar", "Carregamento Eletrico", "Telecomunicacoes", "Alarmes", "Outro"].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* ===== Campos condicionais "Meu Cliente" ===== */}
            {newLead.source === 'meu_cliente' && (
              <div className="border-2 border-indigo-200 bg-indigo-50/50 rounded-lg p-3 space-y-3" data-testid="meu-cliente-block">
                <div className="text-xs font-semibold text-indigo-700 flex items-center gap-1">
                  ✨ Cliente prévio ao CRM — preencha o histórico
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Comercializadora Anterior *</Label>
                    <Select
                      value={newLead.previous_commercializer_id}
                      onValueChange={(v) => setNewLead({ ...newLead, previous_commercializer_id: v })}
                    >
                      <SelectTrigger data-testid="new-lead-previous-comer">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="entrada_direta" data-testid="new-lead-entrada-direta">Entrada direta (casa nova / sem fornecimento)</SelectItem>
                        {commercializerOptions.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Data do último contrato (opcional)</Label>
                    <Input
                      type="date"
                      value={newLead.previous_contract_date}
                      onChange={(e) => setNewLead({ ...newLead, previous_contract_date: e.target.value })}
                      data-testid="new-lead-previous-date"
                    />
                  </div>
                </div>
                {newLead.previous_commercializer_id === 'entrada_direta' ? (
                  <p className="text-xs text-indigo-700" data-testid="new-lead-entrada-direta-info">Entrada direta: o cliente passa a ter fornecimento pela primeira vez nesta morada. Não é preciso contrato anterior.</p>
                ) : (
                <div>
                  <Label className="text-xs">Contrato anterior — PDF (opcional, máx 10 MB)</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) { setPreviousContractFile(null); return; }
                      if (f.size > 10 * 1024 * 1024) {
                        toast.error(`Ficheiro demasiado grande (${(f.size / 1024 / 1024).toFixed(1)} MB). Máximo 10 MB.`);
                        e.target.value = '';
                        return;
                      }
                      setPreviousContractFile(f);
                    }}
                    data-testid="new-lead-previous-file"
                  />
                  {previousContractFile && (
                    <p className="text-[11px] text-indigo-700 mt-1">
                      📎 {previousContractFile.name} ({(previousContractFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
                )}
              </div>
            )}
            <div>
              <Label>Notas</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                rows={2}
              />
            </div>
            {canAttributeAngariador && angariadorOptions.length > 0 && (
              <div>
                <Label>Angariador (opcional)</Label>
                <Select
                  value={newLead.angariador_id || '__none__'}
                  onValueChange={(v) => setNewLead({ ...newLead, angariador_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger data-testid="new-lead-angariador"><SelectValue placeholder="Sem angariador" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">— Sem angariador —</SelectItem>
                    {angariadorOptions.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name} {a.email ? `· ${a.email}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Atribui a lead a um angariador para efeitos de performance e comissões.</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="submit-new-lead">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Criar Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Source Dialog */}
      <Dialog open={newSourceOpen} onOpenChange={(o) => { if (!o && !creatingSource) { setNewSourceOpen(false); setNewSourceLabel(''); } }}>
        <DialogContent className="max-w-sm" data-testid="new-source-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">➕ Nova Origem de Lead</DialogTitle>
            <DialogDescription>Cria uma origem personalizada (ex: Stand do Centro Comercial, Programa Rádio, etc.). Será adicionada para todos os utilizadores.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600">Nome da origem *</Label>
              <Input
                value={newSourceLabel}
                onChange={(e) => setNewSourceLabel(e.target.value)}
                placeholder="Ex: TikTok Ads, Stand FNAC, Rádio Renascença..."
                maxLength={60}
                data-testid="new-source-label"
                onKeyDown={(e) => { if (e.key === 'Enter' && !creatingSource) { e.preventDefault(); handleCreateSource(); } }}
              />
              <p className="text-[11px] text-slate-400 mt-1">Inclui emoji no início se quiseres (ex: &quot;🎯 TikTok Ads&quot;)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewSourceOpen(false); setNewSourceLabel(''); }} disabled={creatingSource}>Cancelar</Button>
            <Button
              onClick={handleCreateSource}
              disabled={creatingSource || !newSourceLabel.trim()}
              className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
              data-testid="submit-new-source"
            >
              {creatingSource ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Criar Origem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default LeadPipelinePage;
